import React, { useState, useRef, useEffect, useCallback } from 'react';
import { analyzeWebcamFrame } from '../services/api';

function WebcamCapture({ mode }) {
  const [isActive, setIsActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [latestResult, setLatestResult] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [frameCount, setFrameCount] = useState(0);
  const [error, setError] = useState(null);
  const [complianceHistory, setComplianceHistory] = useState([]);
  const [captureInterval, setCaptureInterval] = useState(3);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);
  const frameCountRef = useRef(0);

  // Stop webcam and cleanup
  const stopWebcam = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    inFlightRef.current = false;
    setIsActive(false);
    setIsStarting(false);
  }, []);

  // Capture and analyze a single frame
  const captureFrame = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    if (video.readyState < 2) return;

    const canvas = canvasRef.current;
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, 640, 480);

    const imageBase64 = canvas.toDataURL('image/jpeg', 0.8);

    inFlightRef.current = true;
    try {
      const result = await analyzeWebcamFrame(imageBase64, mode);

      frameCountRef.current += 1;
      const currentFrame = frameCountRef.current;

      setLatestResult({ ...result, frameNumber: currentFrame });
      setFrameCount(currentFrame);

      setComplianceHistory(prev => {
        const next = [...prev, { frame: currentFrame, score: result.complianceScore }];
        return next.slice(-20);
      });

      if (result.alerts && result.alerts.length > 0) {
        setRecentAlerts(prev => {
          const newAlerts = result.alerts.map(a => ({
            ...a,
            frameNumber: currentFrame,
            timestamp: result.timestamp
          }));
          return [...newAlerts, ...prev].slice(0, 10);
        });
      }
    } catch (err) {
      console.error('Frame analysis error:', err);
      setError(`Analysis failed: ${err.response?.data?.error || err.message}`);
    } finally {
      inFlightRef.current = false;
    }
  }, [mode]);

  // Attach stream to video element once it's rendered, then fire first capture
  useEffect(() => {
    if (!isActive || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = streamRef.current;

    const onReady = () => {
      captureFrame();
    };
    if (video.readyState >= 2) {
      setTimeout(captureFrame, 300);
    } else {
      video.addEventListener('loadeddata', onReady, { once: true });
    }

    return () => {
      video.removeEventListener('loadeddata', onReady);
    };
  }, [isActive, captureFrame]);

  // Periodic capture loop
  useEffect(() => {
    if (!isActive) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(captureFrame, captureInterval * 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [captureInterval, isActive, captureFrame]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Start webcam
  const handleStart = async () => {
    setError(null);
    setPermissionDenied(false);
    setIsStarting(true);
    setLatestResult(null);
    setRecentAlerts([]);
    setFrameCount(0);
    setComplianceHistory([]);
    frameCountRef.current = 0;

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = mediaStream;

      setIsActive(true);
      setIsStarting(false);
    } catch (err) {
      setIsStarting(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
        setError('Camera permission denied. Please allow camera access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else {
        setError(`Camera error: ${err.message}`);
      }
    }
  };

  const handleStop = () => {
    stopWebcam();
  };

  const severityIcon = (severity) => {
    if (severity === 'critical') return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#ef4444">
        <path d="M8 1L1 14h14L8 1zm0 4.5l1 5H7l1-5zm0 7a1 1 0 110-2 1 1 0 010 2z"/>
      </svg>
    );
    if (severity === 'warning') return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#f59e0b">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10a1 1 0 110-2 1 1 0 010 2zm1-4H7V4h2v3z"/>
      </svg>
    );
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="#3b82f6">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.5v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z"/>
      </svg>
    );
  };

  return (
    <div className="live-stream-container">
      {/* Controls Section */}
      <div className="stream-input-section card">
        <h3 className="card-title">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
            <path d="M9 2a4 4 0 00-4 4v1H4a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-1V6a4 4 0 00-4-4z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="9" cy="6" r="2.5" fill="none" stroke="#ef4444" strokeWidth="1.5"/>
            <rect x="3" y="8" width="12" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            <circle cx="9" cy="11.5" r="2" fill="none" stroke="#ef4444" strokeWidth="1.5"/>
          </svg>
          Webcam Real-Time Detection
        </h3>
        <p className="stream-desc">
          Use your browser camera to perform real-time safety compliance detection.
          The AI analyzes frames at your chosen interval.
        </p>

        <div className="webcam-controls-row">
          <div className="webcam-interval-select">
            <label>Capture every</label>
            <select
              value={captureInterval}
              onChange={(e) => setCaptureInterval(Number(e.target.value))}
              disabled={isStarting}
            >
              <option value={2}>2s</option>
              <option value={3}>3s</option>
              <option value={5}>5s</option>
              <option value={10}>10s</option>
            </select>
          </div>

          {!isActive ? (
            <button
              onClick={handleStart}
              disabled={isStarting}
              className="stream-btn stream-btn-start"
            >
              {isStarting ? (
                <>
                  <div className="spinner-small"></div>
                  Starting Camera...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4 2l10 6-10 6V2z"/>
                  </svg>
                  Start Webcam
                </>
              )}
            </button>
          ) : (
            <button onClick={handleStop} className="stream-btn stream-btn-stop">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="3" y="3" width="10" height="10" rx="1"/>
              </svg>
              Stop
            </button>
          )}
        </div>

        {error && (
          <div className="upload-error" style={{ marginTop: 12 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.5v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z"/>
            </svg>
            {error}
          </div>
        )}

        {permissionDenied && (
          <div className="webcam-permission-prompt">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10 2a4 4 0 00-4 4v2H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm0 2a2 2 0 012 2v2H8V6a2 2 0 012-2z"/>
            </svg>
            <div>
              <strong>Camera Permission Required</strong>
              <p>Check your browser's address bar for the camera permission icon and allow access.</p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Live Content */}
      {(isActive || latestResult) && (
        <div className="stream-live-grid">
          {/* Left: Annotated Frame + Raw Webcam Feed */}
          <div className="stream-left">
            {/* Annotated Detection Frame */}
            {latestResult ? (
              <div className="stream-detection-card card">
                <h4 className="card-title">
                  <span className="live-badge">
                    <span className="live-dot"></span>
                    LIVE
                  </span>
                  AI Detection — Frame #{latestResult.frameNumber}
                  <span className="processing-chip">{latestResult.processingTimeMs}ms</span>
                </h4>
                <div className="stream-annotated-img">
                  <img
                    src={latestResult.annotatedImageBase64}
                    alt={`Detection frame ${latestResult.frameNumber}`}
                  />
                </div>
              </div>
            ) : (
              <div className="stream-detection-card card">
                <h4 className="card-title">
                  <span className="live-badge">
                    <span className="live-dot"></span>
                    LIVE
                  </span>
                  AI Detection
                </h4>
                <div className="stream-waiting-frame">
                  <div className="spinner"></div>
                  <p>Capturing first frame from webcam...</p>
                </div>
              </div>
            )}

            {/* Raw Webcam Feed */}
            <div className="stream-video-card card">
              <h4 className="card-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2.5 4A1.5 1.5 0 014 2.5h8A1.5 1.5 0 0113.5 4v8a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 12V4zM6 5.5v5l4.5-2.5L6 5.5z"/>
                </svg>
                Webcam Feed
              </h4>
              <div className="stream-video-wrapper">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="stream-video webcam-video"
                />
              </div>
            </div>
          </div>

          {/* Right: Metrics + Alerts */}
          <div className="stream-right">
            {/* Live Compliance Score */}
            <div className="stream-score-card card">
              <h4 className="card-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1L2 4v4c0 3.5 2.56 6.77 6 7.5 3.44-.73 6-4 6-7.5V4L8 1z"/>
                </svg>
                Live Compliance
              </h4>
              {latestResult ? (
                <div className="stream-score-display">
                  <div className={`stream-score-value ${
                    latestResult.complianceScore >= 80 ? 'score-good' :
                    latestResult.complianceScore >= 50 ? 'score-warning' : 'score-critical'
                  }`}>
                    {latestResult.complianceScore}%
                  </div>
                  <div className="stream-score-meta">
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.totalPersons || 0}</span>
                      <span className="stream-stat-label">Persons</span>
                    </div>
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.helmetsDetected || 0}</span>
                      <span className="stream-stat-label">Helmets</span>
                    </div>
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.vestsDetected || 0}</span>
                      <span className="stream-stat-label">Vests</span>
                    </div>
                    <div className="stream-stat">
                      <span className="stream-stat-val">{latestResult.summary?.violations || 0}</span>
                      <span className="stream-stat-label">Violations</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="stream-score-waiting">
                  <div className="spinner-small"></div>
                  Waiting for first frame...
                </div>
              )}
            </div>

            {/* Compliance Timeline */}
            {complianceHistory.length > 1 && (
              <div className="stream-timeline-card card">
                <h4 className="card-title">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M1 14h14V2h-1v11H1v1zm1-2h1v-4H2v4zm3 0h1V5H5v7zm3 0h1V8H8v4zm3 0h1V3h-1v9z"/>
                  </svg>
                  Compliance Timeline
                </h4>
                <div className="stream-chart">
                  {complianceHistory.map((item, i) => (
                    <div
                      key={i}
                      className="stream-chart-bar"
                      style={{ height: `${item.score}%` }}
                      title={`Frame ${item.frame}: ${item.score}%`}
                    >
                      <div className={`stream-chart-fill ${
                        item.score >= 80 ? 'fill-good' :
                        item.score >= 50 ? 'fill-warning' : 'fill-critical'
                      }`} />
                    </div>
                  ))}
                </div>
                <div className="stream-chart-labels">
                  <span>Frame {complianceHistory[0]?.frame}</span>
                  <span>Frame {complianceHistory[complianceHistory.length - 1]?.frame}</span>
                </div>
              </div>
            )}

            {/* Live Alerts */}
            <div className="stream-alerts-card card">
              <h4 className="card-title">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1.5L1.5 13h13L8 1.5zm0 3.5l1 4H7l1-4zm0 6a.75.75 0 110-1.5.75.75 0 010 1.5z"/>
                </svg>
                Live Alerts
                {recentAlerts.length > 0 && (
                  <span className="alert-count-badge">{recentAlerts.length}</span>
                )}
              </h4>
              <div className="stream-alerts-list">
                {recentAlerts.length === 0 ? (
                  <div className="stream-no-alerts">
                    {isActive ? 'Monitoring for violations...' : 'No alerts recorded'}
                  </div>
                ) : (
                  recentAlerts.map((alert, i) => (
                    <div key={i} className={`stream-alert-item severity-${alert.severity}`}>
                      <div className="stream-alert-icon">
                        {severityIcon(alert.severity)}
                      </div>
                      <div className="stream-alert-content">
                        <span className="stream-alert-msg">{alert.message}</span>
                        <span className="stream-alert-meta">
                          Frame #{alert.frameNumber} &middot; {new Date(alert.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Session Info */}
            <div className="stream-info-card card">
              <div className="stream-info-row">
                <span>Frames Analyzed</span>
                <span className="stream-info-val">{frameCount}</span>
              </div>
              <div className="stream-info-row">
                <span>Capture Interval</span>
                <span className="stream-info-val">{captureInterval}s</span>
              </div>
              <div className="stream-info-row">
                <span>AI Model</span>
                <span className="stream-info-val">COCO-SSD v2</span>
              </div>
              <div className="stream-info-row">
                <span>Status</span>
                <span className={`stream-info-val ${isActive ? 'text-green' : 'text-dim'}`}>
                  {isActive ? 'Analyzing' : 'Stopped'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebcamCapture;
